import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { useCosts } from '../context/CostsContext';
import { useLanguage } from '../i18n';
import PLTable from './statements/PLTable';
import BalanceSheetTable from './statements/BalanceSheetTable';
import RevenueInput from './statements/RevenueInput';
import { calculateFinancials } from '../utils/financialCalculations';
import { calculateMultiYearRevenue } from '../data/propertyRevenueModel';
import { generateWarnings } from '../utils/warnings';
import statementsTranslations from './statements/i18n';
import insightsTranslations from './insights/i18n';
import researchTranslations from './i18n.research';
import './FinancialStatements.css';

// Lazy load heavy components for code splitting
const InvestmentInsights = lazy(() => import('./insights/InvestmentInsights'));
const SensitivityAnalysis = lazy(() => import('./insights/SensitivityAnalysis'));
const ResearchTab = lazy(() => import('./ResearchTab'));

function FinancialStatements() {
  const { costs } = useCosts();
  const { t, registerTranslations } = useLanguage();

  // Register statements translations
  useEffect(() => {
    registerTranslations(statementsTranslations);
    registerTranslations(insightsTranslations);
    registerTranslations(researchTranslations);
  }, [registerTranslations]);

  // Calculate revenue data from costs context
  const revenueData = useMemo(() => {
    const horizonYears = costs.projection?.horizonYears || 15;
    return calculateMultiYearRevenue(
      {
        rentalType: costs.revenue.rentalType,
        rentIncreaseType: costs.projection.rentIncreaseType,
        kaltmietePerSqm: costs.revenue.kaltmietePerSqm,
        mietspiegelRent: costs.revenue.mietspiegelRent,
        kappungsgrenzeRate: costs.revenue.kappungsgrenzeRate,
        cpiGrowthRate: costs.projection.cpiGrowthRate,
        staffelmieteAmount: costs.projection.staffelmieteAmount,
        sqm: costs.property.squareMeters,
        units: costs.property.numberOfUnits,
        vacancyRate: costs.revenue.vacancyRatePercent / 100,
        nightlyRate: costs.revenue.nightlyRate,
        availableNights: costs.revenue.availableNights,
        occupancyRate: costs.revenue.occupancyRate / 100,
        platformFeePercent: costs.revenue.platformFeePercent,
      },
      horizonYears,
      costs.projection.inflationRate
    );
  }, [costs.revenue, costs.projection, costs.property.squareMeters, costs.property.numberOfUnits]);

  const financials = calculateFinancials(costs, revenueData);
  const warnings = useMemo(() => generateWarnings(costs), [costs]);

  const [activeTab, setActiveTab] = useState('pl');
  const [isExporting, setIsExporting] = useState(false);

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      // Dynamic import - PDF libraries only loaded when needed
      const { generatePdfReport } = await import('../utils/pdfExport');
      generatePdfReport(costs, revenueData, financials);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="financial-statements">
        <RevenueInput />

        {/* Contextual warnings */}
        {warnings.length > 0 && (
          <div className="warnings-container">
            {warnings.map((w) => (
              <div key={w.id} className={`warning-banner warning-${w.severity}`}>
                <span className="warning-icon">
                  {w.severity === 'danger' ? '\u26D4' : w.severity === 'warning' ? '\u26A0' : '\u2139'}
                </span>
                <span>{t(w.textKey)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="statements-tabs">
          <button
            className={`tab ${activeTab === 'pl' ? 'active' : ''}`}
            onClick={() => setActiveTab('pl')}
          >
            {t('tabs.pl')}
          </button>
          <button
            className={`tab ${activeTab === 'bs' ? 'active' : ''}`}
            onClick={() => setActiveTab('bs')}
          >
            {t('tabs.bs')}
          </button>
          <button
            className={`tab ${activeTab === 'both' ? 'active' : ''}`}
            onClick={() => setActiveTab('both')}
          >
            {t('tabs.both')}
          </button>
          <button
            className={`tab ${activeTab === 'research' ? 'active' : ''}`}
            onClick={() => setActiveTab('research')}
          >
            {t('tabs.research')}
          </button>
          <button
            className="export-pdf-btn"
            onClick={handleExportPdf}
            disabled={isExporting}
            title={t('tabs.exportPdf')}
          >
            {isExporting ? '...' : t('tabs.exportPdf')}
          </button>
        </div>

        <div className="statements-content">
          {activeTab === 'pl' && (
            <div className="single-statement">
              <PLTable financials={financials} />
            </div>
          )}

          {activeTab === 'bs' && (
            <div className="single-statement">
              <BalanceSheetTable financials={financials} />
            </div>
          )}

          {activeTab === 'both' && (
            <div className="side-by-side">
              <div className="statement-column">
                <PLTable financials={financials} />
              </div>
              <div className="statement-column">
                <BalanceSheetTable financials={financials} />
              </div>
            </div>
          )}

          {activeTab === 'research' && (
            <Suspense fallback={<div className="loading-placeholder">{t('common.loading') || 'Loading...'}</div>}>
              <ResearchTab />
            </Suspense>
          )}

        </div>
      </div>

      {/* Investment Insights - lazy loaded */}
      <Suspense fallback={<div className="loading-placeholder">{t('common.loading') || 'Loading analysis...'}</div>}>
        <InvestmentInsights revenueData={revenueData} />
      </Suspense>

      {/* Sensitivity Analysis - lazy loaded */}
      <Suspense fallback={<div className="loading-placeholder">{t('common.loading') || 'Loading analysis...'}</div>}>
        <SensitivityAnalysis revenueData={revenueData} />
      </Suspense>
    </>
  );
}

export default FinancialStatements;
